/**
 * "Напоминания" — the follow-ups inbox. Opened via the `reminders:open` app
 * event (sidebar bell). Groups the user's follow-ups by urgency (overdue /
 * today / scheduled / no-time / done) and lets them open the source, snooze,
 * mark done, or remove. Mirrors the in-session design mock.
 */
import { useEffect, useState } from 'react';
import { Modal, Avatar } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useDataStore } from '@/stores/dataStore';
import { onAppEvent, emitAppEvent } from '@/lib/appEvents';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { useFollowUps } from '@/hooks/useFollowUps';
import { HERO_AVATAR_FALLBACK, avatarOrbUrl } from '@/heroui/avatarGradient';
import { Tooltip } from '@/components/Tooltip';
import { AlarmClockIcon, CheckIcon, ClockIcon, CloseIcon, ReplyIcon } from '@/components/icons';
import type { FollowUpItem } from '@bunker/shared';

type Group = 'overdue' | 'today' | 'scheduled' | 'none' | 'done';
const ORDER: Group[] = ['overdue', 'today', 'scheduled', 'none', 'done'];

function groupOf(f: FollowUpItem): Group {
  if (f.status === 'done') return 'done';
  if (!f.remindAt) return 'none';
  const at = new Date(f.remindAt).getTime();
  if (at <= Date.now()) return 'overdue';
  const eod = new Date();
  eod.setHours(23, 59, 59, 999);
  return at <= eod.getTime() ? 'today' : 'scheduled';
}

function TimeChip({ f }: { f: FollowUpItem }) {
  const { t } = useTranslation();
  const g = groupOf(f);
  if (g === 'done') return null;
  const cls =
    g === 'overdue'
      ? 'bg-danger/15 text-danger'
      : g === 'today'
        ? 'bg-primary/15 text-primary'
        : 'bg-default/60 text-muted';
  const label =
    g === 'overdue'
      ? t('reminders.overdue')
      : g === 'none'
        ? t('reminders.none')
        : g === 'today'
          ? new Date(f.remindAt!).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
          : new Date(f.remindAt!).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium tabular-nums ${cls}`}>
      <ClockIcon className="h-3 w-3" />
      {label}
    </span>
  );
}

export function RemindersModal() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { items, loading, error, reload, update, remove } = useFollowUps(open, (e) =>
    toast.error(...errToToast(e, t('reminders.actionFailed'))),
  );
  const selectDirectChannel = useDataStore((s) => s.selectDirectChannel);
  const selectChannel = useDataStore((s) => s.selectChannel);
  const selectGuild = useDataStore((s) => s.selectGuild);
  const guilds = useDataStore((s) => s.guilds);
  const dmChannels = useDataStore((s) => s.dmChannels);

  useEffect(() => onAppEvent('reminders:open', () => setOpen(true)), []);

  /** Is the source still somewhere I can go? (Left the group, lost the
   *  channel: the server already hides the message; don't navigate into it.) */
  function canOpen(f: FollowUpItem): boolean {
    if (f.location.kind === 'channel') {
      const gid = f.location.guildId;
      return !!gid && !!guilds[gid]?.channels.some((c) => c.id === f.location.channelId);
    }
    return !!dmChannels[f.location.channelId];
  }

  function openSource(f: FollowUpItem) {
    if (!canOpen(f)) return;
    if (f.location.kind === 'channel' && f.location.guildId) {
      selectGuild(f.location.guildId);
      selectChannel(f.location.channelId);
    } else {
      selectDirectChannel(f.location.channelId);
    }
    // Let the channel mount, then jump to the message.
    setTimeout(() => emitAppEvent('chat:scroll-to-message', { messageId: f.messageId }), 60);
    setOpen(false);
  }

  const grouped = ORDER.map((g) => ({ g, list: items.filter((f) => groupOf(f) === g) })).filter((x) => x.list.length);
  const dueCount = items.filter((f) => f.status !== 'done' && (!f.remindAt || new Date(f.remindAt).getTime() <= Date.now())).length;

  return (
    <Modal.Backdrop isOpen={open} onOpenChange={setOpen}>
      <Modal.Container size="md" placement="top">
        <Modal.Dialog aria-label={t('reminders.title')} className="overflow-hidden p-0">
          <div className="flex items-center gap-2 border-b border-separator py-1.5 pl-5 pr-2">
            <AlarmClockIcon className="h-[18px] w-[18px] text-ink" />
            <span className="text-sm font-medium text-ink">{t('reminders.title')}</span>
            {dueCount > 0 && (
              <span className="rounded-md bg-danger/15 px-2 py-0.5 text-[11px] font-medium text-danger">
                {t('reminders.dueCount', { count: dueCount })}
              </span>
            )}
            <button
              type="button"
              aria-label={t('common.close')}
              onClick={() => setOpen(false)}
              className="ml-auto grid size-11 shrink-0 place-items-center rounded-lg text-ink-muted transition-colors hover:bg-default/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <CloseIcon className="size-4" />
            </button>
          </div>

          <div className="scrollbar-thin max-h-[60vh] overflow-y-auto py-1">
            {loading && !items.length ? (
              <p className="px-5 py-8 text-center text-sm text-muted">{t('common.loading')}</p>
            ) : !items.length && error ? (
              <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
                <p className="text-sm text-muted">{t('reminders.loadError')}</p>
                <button
                  type="button"
                  onClick={() => void reload()}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {t('reminders.retry')}
                </button>
              </div>
            ) : !items.length ? (
              <p className="px-5 py-10 text-center text-sm text-muted">{t('reminders.empty')}</p>
            ) : (
              grouped.map(({ g, list }) => (
                <section key={g}>
                  <div
                    className={
                      'px-5 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide ' +
                      (g === 'overdue' ? 'text-danger' : 'text-muted')
                    }
                  >
                    {t(`reminders.${g}`)}
                  </div>
                  {list.map((f) => (
                    <div key={f.id} className="app-reminder-row group/row flex items-center gap-3 px-5 py-2 hover:bg-default/30">
                      <button
                        type="button"
                        onClick={() => openSource(f)}
                        disabled={!canOpen(f)}
                        className="app-reminder-main flex min-h-11 min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-default disabled:opacity-60"
                      >
                        <Avatar size="sm">
                          <Avatar.Image src={f.location.avatarUrl ?? avatarOrbUrl(f.location.channelId)} alt="" />
                          <Avatar.Fallback className={HERO_AVATAR_FALLBACK}>
                            {f.location.initials.charAt(0).toUpperCase()}
                          </Avatar.Fallback>
                        </Avatar>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className={'app-reminder-message truncate text-sm ' + (f.status === 'done' ? 'text-muted line-through' : 'text-ink')}>
                            {f.message ? f.message.content || t('reminders.attachmentOnly') : t('reminders.deleted')}
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="truncate text-xs text-muted">{f.location.title}</span>
                            <TimeChip f={f} />
                          </span>
                        </span>
                      </button>
                      <div className="app-reminder-actions flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100 [@media(pointer:coarse)]:opacity-100">
                        {f.status === 'done' ? (
                          <RowBtn label={t('reminders.reopen')} onClick={() => void update(f.id, { status: 'pending' })}>
                            <ReplyIcon className="h-4 w-4" />
                          </RowBtn>
                        ) : (
                          <>
                            <RowBtn
                              label={t('reminders.snooze')}
                              onClick={() => void update(f.id, { remindAt: new Date(Date.now() + 3_600_000).toISOString() })}
                            >
                              <ClockIcon className="h-4 w-4" />
                            </RowBtn>
                            <RowBtn label={t('reminders.doneAction')} onClick={() => void update(f.id, { status: 'done' })}>
                              <CheckIcon className="h-4 w-4 text-success" />
                            </RowBtn>
                          </>
                        )}
                        <RowBtn label={t('reminders.remove')} onClick={() => void remove(f.id)}>
                          <CloseIcon className="h-4 w-4" />
                        </RowBtn>
                      </div>
                    </div>
                  ))}
                </section>
              ))
            )}
          </div>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

function RowBtn(props: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <Tooltip label={props.label}>
      <button
        type="button"
        aria-label={props.label}
        onClick={props.onClick}
        className="grid h-9 w-9 place-items-center rounded-lg text-muted transition-colors hover:bg-default/60 hover:text-ink [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
      >
        {props.children}
      </button>
    </Tooltip>
  );
}
